import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaArrowRight, FaCheck, FaChevronDown, FaStar } from "react-icons/fa";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1600&q=80";

const STATS = [
  { value: 100, suffix: "+", label: "Projects Delivered" },
  { value: 50, suffix: "+", label: "Clients Served" },
  { value: 15, suffix: "+", label: "Years Experience" },
];

const HIGHLIGHTS = [
  "Project & Programme Management",
  "Test Management & Quality Assurance",
  "Enterprise Integration Expertise",
  "Digital Transformation Specialists",
  "Website Development & Digital Marketing",
  "Banking, Payments & Housing Experience",
];

const reduceMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Counts 0 → target once on mount, easing out so the number decelerates
 * into place instead of stopping dead. Honours reduced-motion by starting
 * at the final value.
 */
function useCountUp(target, duration = 1600, delay = 0) {
  const [n, setN] = useState(() => (reduceMotion() ? target : 0));

  useEffect(() => {
    if (reduceMotion()) return undefined;

    let raf;
    let startedAt;
    const timer = setTimeout(() => {
      const step = (now) => {
        // Compare against undefined, not falsiness: a first-frame timestamp
        // of 0 would otherwise re-baseline every frame, pinning p at 0 and
        // spinning the rAF loop forever.
        if (startedAt === undefined) startedAt = now;
        const p = Math.min(1, (now - startedAt) / duration);
        setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [target, duration, delay]);

  return n;
}

function Stat({ value, suffix, label, delay }) {
  const n = useCountUp(value, 1600, delay);
  return (
    <Col xs={4}>
      <div
        className="hero-rise"
        style={{ animationDelay: `${delay}ms`, whiteSpace: "nowrap" }}
      >
        <div
          className="fw-bold"
          style={{
            fontSize: "clamp(1.7rem, 3.4vw, 2.6rem)",
            lineHeight: 1.1,
            background: "linear-gradient(135deg,#ffffff 20%,#ffd0bb 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {n}
          {suffix}
        </div>
        <small
          style={{
            color: "#cbd5e1",
            fontSize: "0.82rem",
            letterSpacing: "0.02em",
          }}
        >
          {label}
        </small>
      </div>
    </Col>
  );
}

function HeroSection() {
  return (
    <section className="hero" style={{ position: "relative", overflow: "hidden" }}>
      <style>{`
        .hero {
          /* The sticky navbar sits in normal flow, so a flat 100vh would
             overflow the viewport by exactly its height. */
          min-height: calc(100vh - 76px);
          display: flex;
          align-items: center;
          padding: 110px 0 90px;
          color: #fff;
          background:
            radial-gradient(90rem 45rem at 82% 8%, rgba(255,107,53,0.30), transparent 55%),
            radial-gradient(70rem 45rem at 8% 92%, rgba(56,132,255,0.22), transparent 55%),
            linear-gradient(140deg, rgba(6,11,24,0.94) 0%, rgba(15,23,42,0.90) 45%, rgba(9,15,32,0.96) 100%),
            url('${HERO_IMAGE}') center/cover no-repeat;
        }
        /* Faint blueprint grid — reads as engineering rather than decoration */
        .hero__grid {
          position: absolute; inset: 0; pointer-events: none;
          background-image:
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px);
          background-size: 64px 64px;
          mask-image: radial-gradient(75% 65% at 50% 40%, #000 30%, transparent 100%);
          -webkit-mask-image: radial-gradient(75% 65% at 50% 40%, #000 30%, transparent 100%);
        }
        .hero__orb {
          position: absolute; border-radius: 50%; pointer-events: none;
          filter: blur(70px); opacity: 0.5;
        }
        .hero__orb--a {
          width: 30rem; height: 30rem; top: -8rem; right: -8rem;
          background: radial-gradient(circle, #ff6b35, transparent 68%);
          animation: heroFloat 13s ease-in-out infinite;
        }
        .hero__orb--b {
          width: 24rem; height: 24rem; bottom: -7rem; left: -6rem;
          background: radial-gradient(circle, #fbbf24, transparent 68%);
          animation: heroFloat 17s ease-in-out infinite reverse;
        }

        @keyframes heroFloat {
          0%,100% { transform: translate3d(0,0,0) scale(1); }
          50%     { transform: translate3d(-2.5rem,2rem,0) scale(1.12); }
        }
        @keyframes heroRise {
          from { opacity: 0; transform: translateY(26px); }
          to   { opacity: 1; transform: none; }
        }
        @keyframes heroPulse {
          0%,100% { opacity: 1; transform: scale(1); }
          50%     { opacity: .35; transform: scale(.75); }
        }

        /* Staggered entrance; children set their own animation-delay */
        .hero-rise { opacity: 0; animation: heroRise .8s cubic-bezier(.22,1,.36,1) forwards; }

        .hero__badge {
          display: inline-flex; align-items: center; gap: 10px;
          padding: 9px 20px; margin-bottom: 26px;
          border-radius: 999px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.18);
          backdrop-filter: blur(10px);
          font-weight: 600; font-size: .84rem; letter-spacing: .06em;
          text-transform: uppercase; color: #ffd8c6;
        }
        .hero__dot {
          width: 8px; height: 8px; border-radius: 50%; background: #ff6b35;
          box-shadow: 0 0 0 4px rgba(255,107,53,0.25);
          animation: heroPulse 2.4s ease-in-out infinite;
        }

        .hero__accent {
          background: linear-gradient(105deg,#ff9066 0%,#ff6b35 45%,#fbbf24 100%);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .hero__cta, .hero__ghost {
          border-radius: 14px; font-weight: 700;
          transition: transform .22s ease, box-shadow .22s ease, background .22s ease;
        }
        .hero__cta {
          background: linear-gradient(135deg,#ff6b35,#ff9066);
          border: none; color: #0f172a;
          box-shadow: 0 12px 30px rgba(255,107,53,0.42);
        }
        .hero__cta:hover, .hero__cta:focus-visible {
          background: linear-gradient(135deg,#ff7d4d,#ffa07d);
          transform: translateY(-3px);
          box-shadow: 0 18px 40px rgba(255,107,53,0.55);
        }
        .hero__cta svg { transition: transform .22s ease; }
        .hero__cta:hover svg { transform: translateX(5px); }

        .hero__ghost {
          background: rgba(255,255,255,0.06);
          border: 1.5px solid rgba(255,255,255,0.45); color: #fff;
          backdrop-filter: blur(8px);
        }
        .hero__ghost:hover, .hero__ghost:focus-visible {
          background: rgba(255,255,255,0.14);
          border-color: #fff; color: #fff; transform: translateY(-3px);
        }

        .hero__card {
          position: relative;
          background: rgba(255,255,255,0.97);
          border: 1px solid rgba(255,255,255,0.6);
          border-radius: 22px;
          box-shadow: 0 30px 70px rgba(0,0,0,0.45);
          backdrop-filter: blur(14px);
          color: #0f172a;
        }
        .hero__ribbon {
          position: absolute; top: -16px; right: 22px;
          display: inline-flex; align-items: center; gap: 7px;
          padding: 8px 16px; border-radius: 999px;
          background: linear-gradient(135deg,#0f172a,#1e293b);
          color: #fbbf24; font-size: .76rem; font-weight: 700;
          letter-spacing: .04em;
          box-shadow: 0 10px 24px rgba(0,0,0,0.35);
        }
        .hero__item {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 9px 10px; margin: 0 -10px;
          border-radius: 11px;
          font-size: .95rem; color: #1e293b;
          transition: background .18s ease, transform .18s ease;
        }
        .hero__item:hover { background: #fff5f0; transform: translateX(4px); }
        .hero__tick {
          flex-shrink: 0; margin-top: 2px;
          width: 21px; height: 21px; border-radius: 50%;
          display: grid; place-items: center;
          background: linear-gradient(135deg,#ff6b35,#ff9066);
          color: #fff; font-size: .6rem;
        }

        .hero__scroll {
          position: absolute; left: 50%; bottom: 22px;
          transform: translateX(-50%);
          display: flex; flex-direction: column; align-items: center; gap: 6px;
          color: rgba(255,255,255,0.6);
          font-size: .68rem; letter-spacing: .18em; text-transform: uppercase;
          animation: heroFloat 3.2s ease-in-out infinite;
        }

        /* Hard breaks are a desktop nicety — below sm they force lines wider
           than the viewport and the headline gets clipped. Let it wrap. */
        .hero__br { display: none; }
        @media (min-width: 576px) { .hero__br { display: inline; } }

        @media (max-width: 991.98px) {
          .hero { min-height: auto; padding: 90px 0 70px; }
          .hero__scroll { display: none; }
        }
        /* Motion is decoration here — none of it carries meaning */
        @media (prefers-reduced-motion: reduce) {
          .hero *, .hero-rise { animation: none !important; opacity: 1 !important; }
          .hero__cta:hover, .hero__ghost:hover, .hero__item:hover { transform: none; }
        }
      `}</style>

      <div className="hero__grid" />
      <div className="hero__orb hero__orb--a" />
      <div className="hero__orb hero__orb--b" />

      <Container style={{ position: "relative", zIndex: 1 }}>
        <Row className="align-items-center">
          {/* Left — headline, CTAs, proof */}
          <Col lg={7} className="mb-5 mb-lg-0 text-center text-lg-start">
            <span className="hero__badge hero-rise">
              <span className="hero__dot" />
              Technology • Transformation • Digital Excellence
            </span>

            <h1
              className="hero-rise"
              style={{
                animationDelay: "90ms",
                fontSize: "clamp(1.95rem, 5vw, 4.1rem)",
                fontWeight: 800,
                lineHeight: 1.08,
                letterSpacing: "-0.02em",
                maxWidth: "820px",
                marginBottom: 0,
                overflowWrap: "break-word",
              }}
            >
              Delivering Technology,
              <br className="hero__br" />{" "}
              Transformation &amp;
              <br className="hero__br" />{" "}
              <span className="hero__accent">Digital Excellence</span>
            </h1>

            <p
              className="mt-4 hero-rise mx-auto mx-lg-0"
              style={{
                animationDelay: "180ms",
                fontSize: "1.12rem",
                color: "#dbe3ee",
                maxWidth: "620px",
                lineHeight: 1.85,
              }}
            >
              We help organizations successfully deliver complex programmes,
              digital transformations, enterprise integrations, website
              solutions, and customer-focused technology initiatives that
              create measurable business outcomes.
            </p>

            <div
              className="mt-4 hero-rise d-flex flex-wrap gap-3 justify-content-center justify-content-lg-start"
              style={{ animationDelay: "270ms" }}
            >
              <Button
                as={Link}
                to="/contact"
                size="lg"
                className="hero__cta px-4 py-3 d-inline-flex align-items-center gap-2"
              >
                Schedule Consultation
                <FaArrowRight />
              </Button>

              <Button
                as={Link}
                to="/services"
                size="lg"
                className="hero__ghost px-4 py-3"
              >
                Explore Services
              </Button>
            </div>

            <hr
              className="hero-rise"
              style={{
                animationDelay: "330ms",
                margin: "38px 0 22px",
                borderColor: "rgba(255,255,255,0.16)",
                opacity: 1,
              }}
            />

            <Row className="text-center text-lg-start">
              {STATS.map((s, i) => (
                <Stat key={s.label} {...s} delay={380 + i * 110} />
              ))}
            </Row>
          </Col>

          {/* Right — capability card */}
          <Col lg={5} className="mt-5 mt-lg-0">
            <div
              className="hero__card p-4 p-md-5 hero-rise"
              style={{ animationDelay: "220ms" }}
            >
              <span className="hero__ribbon">
                <FaStar />
                15+ YEARS
              </span>

              <h2
                className="fw-bold mb-1"
                style={{ fontSize: "1.6rem", letterSpacing: "-0.01em" }}
              >
                Why Choose Sarvavya?
              </h2>
              <p style={{ color: "#64748b", fontSize: "0.92rem", marginBottom: "18px" }}>
                Delivery expertise across regulated, high-stakes industries.
              </p>

              <div style={{ marginBottom: "22px" }}>
                {HIGHLIGHTS.map((item) => (
                  <div key={item} className="hero__item">
                    <span className="hero__tick">
                      <FaCheck />
                    </span>
                    {item}
                  </div>
                ))}
              </div>

              <Button
                as={Link}
                to="/contact"
                className="hero__cta w-100 py-3 d-inline-flex align-items-center justify-content-center gap-2"
                style={{ fontSize: "1.02rem" }}
              >
                Get Started Today
                <FaArrowRight />
              </Button>
            </div>
          </Col>
        </Row>
      </Container>

      <div className="hero__scroll">
        Scroll
        <FaChevronDown />
      </div>
    </section>
  );
}

export default HeroSection;
